import { useMemo, useRef } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import InsightsAPI from '@/features/dashboard/api/insights/InsightsAPI'
import Alert from '@/shared/components/Alert/Alert'
import Button from '@/shared/components/Button/Button'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'

interface AutoTranslateButtonProps {
  defaultLanguage: string
  allLanguages: string[]
  syncFieldNames: string[]
}

// Translation progress is kept in the form itself (`_isTranslating`,
// `_translationComplete`) so the button and the status banner can live in two
// different places in the layout: the button sits next to the language tabs, or
// in the modal's action row, and the banner stays at the top of the form where
// alerts belong.
//
// Light fill, everywhere it appears. It sits beside the form's own submit and
// cancel, which are filled too, so an outline made it read as the odd one out.
export function AutoTranslateButton({
  defaultLanguage,
  allLanguages,
  syncFieldNames
}: AutoTranslateButtonProps) {
  const { t } = useTranslation()
  const { setValue, control } = useFormContext()
  const isTranslating = useWatch({ control, name: '_isTranslating' })
  const requestIdRef = useRef(0)

  // Watch default-language values
  const defaultLangPaths = syncFieldNames.map(
    name => `${name}.${defaultLanguage}`
  )
  const defaultLangValues = useWatch({ control, name: defaultLangPaths })

  // Watch ALL other-language values for every sync field to compute disabled state
  const otherLanguages = allLanguages.filter(l => l !== defaultLanguage)
  const allOtherPaths = syncFieldNames.flatMap(name =>
    otherLanguages.map(lang => `${name}.${lang}`)
  )
  const allOtherValues = useWatch({ control, name: allOtherPaths })

  // Compute whether button should be disabled
  const buttonDisabled = useMemo(() => {
    if (otherLanguages.length === 0) return true

    // All translatable fields must have a default-language value filled
    const hasAllDefaultValues = defaultLangValues.every(
      (v: unknown) => typeof v === 'string' && v.trim().length > 0
    )
    if (!hasAllDefaultValues) return true

    // If all other-language fields are already filled, disable
    if (allOtherValues.length > 0) {
      const allOtherFilled = allOtherValues.every(
        (v: unknown) => typeof v === 'string' && v.trim().length > 0
      )
      if (allOtherFilled) return true
    }

    return false
  }, [defaultLangValues, allOtherValues, otherLanguages.length])

  const handleTranslate = async () => {
    const id = ++requestIdRef.current
    setValue('_isTranslating', true)
    setValue('_translationComplete', false)

    const payload = syncFieldNames.reduce<Record<string, unknown>[]>(
      (acc, name, i) => {
        const text = defaultLangValues[i]
        if (typeof text === 'string' && text.trim()) {
          acc.push({
            inputName: name,
            translations: allLanguages.map(lang => ({
              languageCode: lang,
              value: lang === defaultLanguage ? text : ''
            }))
          })
        }
        return acc
      },
      []
    )

    if (!payload.length) {
      setValue('_isTranslating', false)
      return
    }

    try {
      const items = await InsightsAPI.translateToAllLanguages(payload)
      if (id !== requestIdRef.current) return

      for (const item of items) {
        for (const entry of item.translations) {
          if (entry.languageCode !== defaultLanguage && entry.value != null) {
            setValue(`${item.inputName}.${entry.languageCode}`, entry.value, {
              shouldDirty: true
            })
          }
        }
      }
      if (id === requestIdRef.current) {
        setValue('_translationComplete', true)
      }
    } catch {
      // translation failed silently
    } finally {
      if (id === requestIdRef.current) {
        setValue('_isTranslating', false)
      }
    }
  }

  return (
    <Button
      label={t('SIDEBAR_ACCORDION.CATEGORY.AUTOMATIC_TRANSLATION')}
      variant="contained"
      color="primary"
      disabled={buttonDisabled || !!isTranslating}
      onClick={handleTranslate}
      icon={<AutoAwesomeRoundedIcon sx={{ height: 20, width: 20 }} />}
      iconPosition="end"
    />
  )
}

// Status banner for the translation triggered by AutoTranslateButton. Passed to
// DynamicForm as `FormEffects`, so it renders at the top of the form.
export function AutoTranslateFormEffect() {
  const { t } = useTranslation()
  const { setValue, control } = useFormContext()
  const isTranslating = useWatch({ control, name: '_isTranslating' })
  const isTranslationComplete = useWatch({
    control,
    name: '_translationComplete'
  })

  if (!isTranslating && !isTranslationComplete) return null

  return (
    <Alert
      severity={isTranslating ? 'info' : 'success'}
      message={
        isTranslating
          ? t(
              'SIDEBAR_ACCORDION.CATEGORY.AUTOMATIC_TRANSLATION_IN_PROGRESS',
              'Automatic translation in progress...'
            )
          : t(
              'SIDEBAR_ACCORDION.CATEGORY.AUTOMATIC_TRANSLATION_COMPLETE',
              'Translation completed successfully'
            )
      }
      onClose={() => {
        setValue('_isTranslating', false)
        setValue('_translationComplete', false)
      }}
    />
  )
}
